import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArchiveToggle } from "@/components/ArchiveToggle";
import { labelFor } from "@/lib/categories";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, X, Bot, User, RefreshCw, History, Download, ShieldAlert } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useEstablishment } from "@/context/EstablishmentContext";

const DECISION_META = {
  auto_matched: { labelKey: "mh.autoMatch", cls: "bg-teal-100 text-teal-700", Icon: Bot },
  confirmed: { labelKey: "c.confirmed", cls: "bg-indigo-100 text-findo-indigo", Icon: Check },
  rejected: { labelKey: "mh.rejected", cls: "bg-rose-100 text-rose-700", Icon: X },
  blocked_contradiction: { labelKey: "mh.blocked", cls: "bg-amber-100 text-amber-700", Icon: ShieldAlert },
};
const SOURCE_LABEL = { visual: "mh.srcVisual", text: "mh.srcText", manual: "mh.srcManual" };

function fmtDate(iso, locale) {
  try { return new Date(iso).toLocaleString(locale || "en", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function MatchHistory() {
  const { t, lang } = useLang();
  const { selectedId } = useEstablishment();
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [category, setCategory] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  const estQs = selectedId && selectedId !== "all" ? `&est=${selectedId}` : "";
  const load = () => api(`/matching/events?limit=200${estQs}`).then(setEvents).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [selectedId]);

  async function exportCsv() {
    try {
      const qs = new URLSearchParams();
      if (filter !== "all") qs.set("decision", filter);
      if (period !== "all") qs.set("days", period);
      if (category !== "all") qs.set("category", category);
      if (selectedId && selectedId !== "all") qs.set("est", selectedId);
      const res = await fetch(`${API}/matching/events/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(t("mh.exportImpossible"));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "findloop-journal-ia.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("mh.exportDone"));
    } catch (e) { toast.error(e.message); }
  }

  const cutoff = period === "all" ? 0 : Date.now() - Number(period) * 864e5;
  const categories = Array.from(new Set((events || []).map((e) => e.category).filter(Boolean)));
  const filtered = (events || []).filter((e) =>
    (showArchived ? e.archived : !e.archived) &&
    (filter === "all" || e.decision === filter) &&
    (category === "all" || e.category === category) &&
    (period === "all" || new Date(e.created_at).getTime() >= cutoff)
  );
  const activeCount = (events || []).filter((e) => !e.archived).length;
  const archivedCount = (events || []).filter((e) => e.archived).length;
  const counts = (events || []).filter((e) => (showArchived ? e.archived : !e.archived))
    .reduce((a, e) => ({ ...a, [e.decision]: (a[e.decision] || 0) + 1 }), {});

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <PageHeader title={t("mh.title")} subtitle={t("mh.subtitle")} testid="match-history-header" />
        <div className="mt-2 flex items-center gap-3">
          <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-sm font-semibold text-findo-indigo hover:underline" data-testid="match-history-export">
            <Download size={15} /> {t("mh.exportCsv")}
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-findo-indigo" data-testid="match-history-refresh">
            <RefreshCw size={15} /> {t("mh.refresh")}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <ArchiveToggle showArchived={showArchived} onChange={setShowArchived}
          activeCount={activeCount} archivedCount={archivedCount} t={t} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="match-history-filters">
        {[["all", t("c.all")], ["auto_matched", t("mh.autoMatch")], ["confirmed", t("mh.fConfirmed")], ["rejected", t("mh.fRejected")], ["blocked_contradiction", t("mh.fBlocked")]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === k ? "bg-findo-indigo text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {l}{k !== "all" && counts[k] ? ` (${counts[k]})` : ""}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="filter-period"
          className="rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-findo-indigo/30">
          <option value="all">{t("mh.allPeriod")}</option>
          <option value="7">{t("mh.days7")}</option>
          <option value="30">{t("mh.days30")}</option>
          <option value="90">{t("mh.days90")}</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category"
          className="rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-findo-indigo/30">
          <option value="all">{t("mh.allCategories")}</option>
          {categories.map((c) => <option key={c} value={c}>{labelFor(c)}</option>)}
        </select>
      </div>

      {events === null ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="match-history-empty">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-indigo/10 text-findo-indigo"><History size={32} /></div>
          <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{t("mh.emptyTitle")}</h3>
          <p className="mt-1 text-slate-500 text-sm">{t("mh.emptyDesc")}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="match-history-list">
          {filtered.map((e) => {
            const m = DECISION_META[e.decision] || DECISION_META.confirmed;
            const pct = e.score != null ? Math.round(e.score * 100) : null;
            return (
              <div key={e.id} className="bg-white rounded-2xl border border-border p-4 card-shadow flex items-start gap-4" data-testid={`match-event-${e.id}`}>
                <div className={`h-10 w-10 shrink-0 grid place-items-center rounded-xl ${m.cls}`}><m.Icon size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={`${m.cls} border-0`} data-testid={`event-decision-${e.id}`}>{t(m.labelKey)}</Badge>
                    {pct != null && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`event-score-${e.id}`}>{pct}%</span>
                    )}
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      {e.source === "visual" ? <Sparkles size={12} /> : e.source === "text" ? <User size={12} /> : null}
                      {SOURCE_LABEL[e.source] ? t(SOURCE_LABEL[e.source]) : e.source}
                    </span>
                    <span className="ml-auto text-xs text-slate-400">{fmtDate(e.created_at, lang)}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-800">
                    <span className="font-semibold">{e.customer_name || t("dec.labelClient")}</span>
                    {e.category ? <span className="text-slate-500"> · {labelFor(e.category)}</span> : null}
                  </p>
                  {e.item_description && <p className="text-xs text-slate-500 mt-0.5">{t("mh.objectLabel")} {e.item_description}</p>}
                  {e.reason && <p className="mt-1 text-xs text-slate-600 italic">« {e.reason} »</p>}
                  {e.decision === "blocked_contradiction" && (
                    <p className="mt-1 text-xs text-amber-700">{t("mh.blockedReason")}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
